/**
 * DeepRootSkillEffect — Earth tree wall path passive (wall_path_passive).
 *
 * While the player has been stationary for at least 1000ms, all incoming
 * knockback and stagger are completely negated. Any physics impulse that
 * would shift the player's position is zeroed out. Any stagger application
 * via StatusEffectSystem is rejected before it fires.
 *
 * Immunity covers knockback and stagger only. Stun, root, and all higher
 * CC tiers still apply regardless of stationary duration.
 *
 * The effect is always "on" once unlocked — there is no activation.
 * `CombatHitResolver` queries `isKnockbackImmune` before applying physics
 * impulses. `StatusEffectSystem` queries `isStaggerImmune` before applying
 * stagger to the player entity.
 *
 * UpgradeEffectResolver key(s):
 * - key: 'knockbackImmune', scope: tree 'earth', value: true, aggregation: boolean
 * - key: 'staggerImmune', scope: tree 'earth', value: true, aggregation: boolean
 * - stationaryRequirementMs: 1000
 *
 * This is the fourth `stationaryRequirementMs: 1000` node in the Earth tree,
 * all sharing the same StationaryTracker instance.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see earth-spec.md § Deep Root (wall_path_passive)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface DeepRootDeps {
  readonly stationary: {
    /**
     * Returns true when the player has been continuously stationary for at
     * least `thresholdMs` milliseconds. The tracker owns the state — Deep
     * Root only queries it.
     */
    isStationary(thresholdMs: number): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface DeepRootConfig {
  /** Minimum stationary duration (ms) before knockback/stagger immunity activates. */
  readonly stationaryRequirementMs: number;
}

const DEFAULT_CONFIG: DeepRootConfig = {
  stationaryRequirementMs: 1000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DeepRootSkillEffect implements SkillEffect {
  readonly skillId = 'wall_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: DeepRootDeps;
  private readonly config: DeepRootConfig;

  constructor(deps: DeepRootDeps, config: Partial<DeepRootConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Config getter ---

  get stationaryRequirementMs(): number { return this.config.stationaryRequirementMs; }

  // --- Immunity query interface ---

  /**
   * Whether the player is currently immune to knockback.
   *
   * `CombatHitResolver` calls this before applying any knockback impulse to
   * the player. When true, the impulse vector is zeroed — no position change.
   */
  get isKnockbackImmune(): boolean {
    return this.deps.stationary.isStationary(this.config.stationaryRequirementMs);
  }

  /**
   * Whether the player is currently immune to stagger.
   *
   * `StatusEffectSystem` calls this before applying stagger to the player
   * entity. When true, the stagger application is rejected before it fires.
   * Stun and other higher CC tiers are NOT blocked — immunity is stagger-only.
   */
  get isStaggerImmune(): boolean {
    return this.deps.stationary.isStationary(this.config.stationaryRequirementMs);
  }

  // --- SkillEffect interface ---

  update(_delta: number): void {
    // Passive — stationary state is owned by StationaryTracker, not this effect
  }

  destroy(): void {
    // No owned resources
  }
}
